import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  MRT_TableInstance,
  MRT_ColumnDef,
  MRT_Row,
  MRT_SortingState,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_Updater,
  MRT_TablePagination,
  MRT_ToggleFullScreenButton,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
} from 'material-react-table';
import {
  Box,
  Typography,
  lighten,
} from '@mui/material';
import { FilterDateRange } from '../hooks/useDateRange';
import { DataScopeResource } from '../hooks/useDataScope';
import ReportPageHeader from './ReportPageHeader';
import { useUI } from '../providers/MoleUIContext';
import { Util } from '../util/Util';
import { canSeeTimestamps, filterColumns } from '../util/access';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo } from 'react';
import { useAppSocket } from '../providers/AppSocketContext';
import { SxProps, Theme } from '@mui/material/styles';
import { Z_INDEX } from '../styles/zIndex';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  columnsHaveTimestamps,
  createTimestampColumns,
} from '../util/timestampColumns';
import { CatalogItemNameCell, CatalogItemPickerProvider } from './CatalogItemActions';

import { roundQty } from "../util/quantity";
import { useSessionState } from '../hooks/useSessionState';
interface Props {
  header?: string;
  data: any[];
  columns: MRT_ColumnDef<any>[];
  modal?: boolean;
  lastUpdated?: Date | null;
  useDateRangeFilter?: boolean;
  period?: FilterDateRange;
  setDateRangeFilter?: Dispatch<SetStateAction<FilterDateRange>>;
  defaultOption?: string;
  /** Record types listed; bounds the date filter by the user's read scope. */
  dataScope?: DataScopeResource | DataScopeResource[];
  totals?: { label: string; key?: string; accessorFn?: (row: any) => number }[];
  isLoading?: boolean;
  enableRowActions?: boolean;
  renderTopToolbarActions?: (table: MRT_TableInstance<any>) => ReactNode;
  renderRowActionMenuItems?: (row: MRT_Row<any>, closeMenu: () => void) => ReactNode[];
  filters?: ReactNode;
  bundle?: any;
  hideColumns?: string[];
  /**
   * Names this table's rows, so the column layer and the timestamps sub can be
   * asked about them: a `resource` when the rows are a table, a `posting` when
   * they are a document. A statement (a situation, a daily aggregate) is neither
   * and declares nothing.
   */
  resource?: string;
  posting?: string;
  getRowSx?: (row: MRT_Row<any>) => SxProps<Theme>;
  renderDetailPanel?: (args: { row: MRT_Row<any> }) => ReactNode;
  includeTimestampColumns?: boolean;
  showHubBack?: boolean;
  defaultSorting?: MRT_SortingState;
  /**
   * Keeps the date range choice, search, sorting, simple column filters and
   * page size for the session under this key (the page's useData/useDateRange key).
   */
  stateKey?: string;
}

const isPlain = (value: unknown): boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value == null;

/** Column filters that survive JSON; date pickers hold Date/dayjs objects, which are left out. */
const storableFilters = (filters: MRT_ColumnFiltersState): MRT_ColumnFiltersState =>
  filters.filter(({ value }) => isPlain(value) || (Array.isArray(value) && value.every(isPlain)));

const applyUpdater = <T,>(updater: MRT_Updater<T>, previous: T): T =>
  typeof updater === 'function' ? (updater as (old: T) => T)(previous) : updater;

const rowDescription = (row: any): string => String(row?.description ?? '').trim();

const TableView = ({ header, data, columns, modal = false, lastUpdated, useDateRangeFilter = true,
  period,
  setDateRangeFilter,
  defaultOption, dataScope, renderTopToolbarActions, enableRowActions = true,
  renderRowActionMenuItems, renderDetailPanel, totals, isLoading, bundle, filters, hideColumns, getRowSx,
  resource, posting,
  includeTimestampColumns = true,
  showHubBack,
  defaultSorting,
  stateKey,
}: Props) => {

  const { setStatusMessage } = useUI();
  const { appSettings } = useAppSocket();
  const { access } = useAuth();
  const { t } = useTranslation();
  // `created` / `updated` are a sub of each table and each document rather than
  // one flag for all of them, so a table has to say what it is. One that does
  // not name a resource or a posting cannot be asked, and the server would send
  // the values only if the sub were granted -- so it offers no column.
  const canViewTimestamps = includeTimestampColumns
    && Boolean(resource || posting)
    && canSeeTimestamps(access, { resource, posting });

  const resolvedColumns = useMemo(() => {
    // The column layer: a column this user may not read leaves the table, its
    // column menu and the export in one move. The backend already omits the
    // value; this is what stops an empty column being offered.
    let cols = filterColumns(columns, access, { resource, posting });
    if (canViewTimestamps && !columnsHaveTimestamps(cols)) {
      cols = [...cols, ...createTimestampColumns(t)];
    }
    return cols.map((col) => {
      const key = String(col.accessorKey || col.id || "");
      if (key !== "item_name" && key !== "item" && key !== "ItemName" && key !== "item_desc") return col;
      const originalCell = col.Cell;
      return {
        ...col,
        Cell: (cellProps: any) => {
          const { cell, row } = cellProps;
          const cellContent = originalCell ? originalCell(cellProps) : String(cell.getValue() ?? "");
          const rawId = row.original.item_id ?? row.original.id ?? (typeof row.original.item === "number" ? row.original.item : undefined) ?? row.original.itemId ?? row.original.Item_Id ?? row.original.productId ?? row.original.product_id;
          const itemId = Number(rawId);
          const itemName = String(
            row.original.item_name ??
            row.original.item_desc ??
            (typeof row.original.item === "string" ? row.original.item : undefined) ??
            row.original.ItemName ??
            cell.getValue() ??
            ""
          );
          if (!Number.isFinite(itemId) || itemId <= 0) return cellContent;
          return (
            <CatalogItemNameCell itemId={itemId} itemName={itemName}>
              {cellContent}
            </CatalogItemNameCell>
          );
        },
      };
    });
  }, [access, canViewTimestamps, columns, posting, resource, t]);

  const useDefaultDescriptionPanel = useMemo(
    () => !renderDetailPanel && data.some((row) => rowDescription(row)),
    [data, renderDetailPanel],
  );

  const resolvedSorting = useMemo<MRT_SortingState>(() => {
    if (defaultSorting && defaultSorting.length > 0) {
      return defaultSorting;
    }
    const dateCol = resolvedColumns.find((col) => {
      const key = String(col.id || col.accessorKey || "");
      return key === "date" || key === "issued";
    });
    if (!dateCol) return [];
    return [{ id: String(dateCol.id || dateCol.accessorKey), desc: true }];
  }, [defaultSorting, resolvedColumns]);

  const tableKey = (part: string) => (stateKey ? `table:${stateKey}:${part}` : null);
  const [globalFilter, setGlobalFilter] = useSessionState<string | undefined>(tableKey('search'), undefined);
  const [sorting, setSorting] = useSessionState<MRT_SortingState | undefined>(tableKey('sorting'), undefined);
  const [columnFilters, setColumnFilters] = useSessionState<MRT_ColumnFiltersState>(tableKey('filters'), [], undefined, storableFilters);
  const [pagination, setPagination] = useSessionState<MRT_PaginationState>(
    tableKey('page'),
    { pageIndex: 0, pageSize: 10 },
    // Only the page size is kept: the table goes back to page 1 whenever rows load.
    (stored) => ({ pageIndex: 0, pageSize: Number(stored?.pageSize) || 10 }),
  );
  const persistedState = stateKey
    ? {
        state: {
          globalFilter,
          sorting: sorting ?? resolvedSorting,
          columnFilters,
          pagination,
        },
        onGlobalFilterChange: (updater: MRT_Updater<any>) =>
          setGlobalFilter((previous) => applyUpdater(updater, previous) || undefined),
        onSortingChange: (updater: MRT_Updater<MRT_SortingState>) =>
          setSorting((previous) => applyUpdater(updater, previous ?? resolvedSorting)),
        onColumnFiltersChange: (updater: MRT_Updater<MRT_ColumnFiltersState>) =>
          setColumnFilters((previous) => applyUpdater(updater, previous)),
        onPaginationChange: (updater: MRT_Updater<MRT_PaginationState>) =>
          setPagination((previous) => applyUpdater(updater, previous)),
      }
    : null;

  let hiddenColumnsObj: Record<string, boolean> = hideColumns
    ? hideColumns.reduce<Record<string, boolean>>((acc, col) => ({ ...acc, [col]: false }), {})
    : {};
  if (canViewTimestamps) {
    hiddenColumnsObj = {
      ...hiddenColumnsObj,
      created: hiddenColumnsObj.created ?? false,
      updated: hiddenColumnsObj.updated ?? false,
    };
  }

  // column visibility conditions
  if (!Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_AGENTS?.value)) {
    hiddenColumnsObj = {
      ...hiddenColumnsObj,
      agent: false,
    };
  }

  if (!Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value)) {
    hiddenColumnsObj = {
      ...hiddenColumnsObj,
      item_code: false,
    };
  }

  const table = useMaterialReactTable({
    columns: resolvedColumns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: enableRowActions,
    enableExpanding: !!renderDetailPanel || useDefaultDescriptionPanel,
    enableRowSelection: true,
    groupedColumnMode: 'reorder',
    // columnFilterDisplayMode: 'popover',
    initialState: {
      density: 'compact',
      showGlobalFilter: true,
      // Restored column filters stay visible so it is clear why rows are hidden.
      showColumnFilters: columnFilters.length > 0,
      sorting: resolvedSorting,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      columnVisibility: {
        ...hiddenColumnsObj,
      },
      // pagination: {
      //   pageSize: 100, // default number of rows per page
      //   pageIndex: 1, // optional: start from first page
      // },
    },
    /* MRT Table Top Element (Paper) */
    muiTablePaperProps: {
      sx: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.getIsSelected()
          ? (theme) => lighten(theme.palette.primary.light, 0.85)
          : 'inherit',
        ...(getRowSx ? getRowSx(row) : {}),
      },
    }),
    muiTableContainerProps: {
      sx: {
        maxHeight: 600,      // limit height so body scrolls
        overflowY: 'auto',   // vertical scroll
      },
    },
    // muiTableBodyRowMenuProps: {
    //   // This affects both the column header menu and the row actions menu
    //   sx: {
    //     zIndex: 2000,        // or any value higher than your modal (1350 → use 1400+)
    //   },
    // },
    muiToolbarAlertBannerProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: Z_INDEX.menu,
        backgroundColor: (theme) => theme.palette.background.default,
      },
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40, 50, 100, 200, 500, 1000],
      shape: 'rounded',
      variant: 'outlined',
    },
    ...(persistedState && {
      onGlobalFilterChange: persistedState.onGlobalFilterChange,
      onSortingChange: persistedState.onSortingChange,
      onColumnFiltersChange: persistedState.onColumnFiltersChange,
      onPaginationChange: persistedState.onPaginationChange,
    }),
    state: {
      ...persistedState?.state,
      isLoading: !!isLoading,
      showProgressBars: !!isLoading,
    },
    muiExpandButtonProps: useDefaultDescriptionPanel
      ? ({ row }) => ({
          sx: {
            display:
              row.getIsGrouped() || rowDescription(row.original) ? 'flex' : 'none',
          },
        })
      : undefined,
    renderDetailPanel: renderDetailPanel
      ? renderDetailPanel
      : useDefaultDescriptionPanel
        ? ({ row }) => {
            if (row.getIsGrouped()) return <></>;
            const description = rowDescription(row.original);
            if (!description) return undefined;
            return (
              <Box
                sx={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  left: '30px',
                  maxWidth: '1000px',
                  position: 'sticky',
                  width: '100%',
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography>{t("common:tableView.detailPanel.description")}:</Typography>
                  <Typography>{description}</Typography>
                </Box>
              </Box>
            );
          }
        : undefined,
    renderRowActionMenuItems: ({ row, closeMenu }) =>
      renderRowActionMenuItems ? renderRowActionMenuItems(row, closeMenu) : [],
    renderTopToolbar: ({ table }) => {
      return (
        <>
          {!bundle && header && (
            <ReportPageHeader
              title={header}
              period={!modal ? period : undefined}
              showPeriod={!modal && !!period}
              lastUpdated={!modal ? lastUpdated : undefined}
              useDateRangeFilter={!modal && useDateRangeFilter}
              setDateRangeFilter={setDateRangeFilter}
              defaultDateRangeOption={defaultOption}
              dateRangeKey={stateKey}
              dataScope={dataScope}
              filters={filters}
              compact
              showHubBack={showHubBack ?? (!modal && !bundle)}
            />
          )}
          <Box
            sx={(theme) => ({
              backgroundColor: lighten(theme.palette.background.default, 0.05),
              display: 'flex',
              gap: '0.5rem',
              p: 1,
              justifyContent: 'space-between',
            })}
          >
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <MRT_GlobalFilterTextField table={table} />
              <MRT_ToggleFiltersButton table={table} />
              <MRT_ToggleDensePaddingButton table={table} />
              <MRT_ShowHideColumnsButton table={table} />
              <MRT_ToggleFullScreenButton table={table} />
              <MRT_TablePagination table={table} />
            </Box>
            <Box>
              {renderTopToolbarActions && renderTopToolbarActions(table)}
            </Box>
          </Box>
        </>
      );
    },
  });


  const selectedRows = table.getSelectedRowModel().rows;

  const selectionStatusMessage = useMemo(() => {
    if (!totals || totals.length === 0 || selectedRows.length === 0) {
      return null;
    }

    const sums = totals.map(({ key, label, accessorFn }) => {
      const sum = selectedRows.reduce((acc, row) => {
        const raw = accessorFn ? accessorFn(row.original) : row.original[key ?? ''];
        const value = Number(raw ?? 0);
        return acc + (Number.isFinite(value) ? value : 0);
      }, 0);
      return `${label}: ${Util.formatNumber(roundQty(sum))}`;
    });

    return sums.join(' | ');
  }, [selectedRows, totals]);

  useEffect(() => {
    if (selectionStatusMessage) {
      setStatusMessage(selectionStatusMessage);
      return () => setStatusMessage(null);
    }
    return undefined;
  }, [selectionStatusMessage, setStatusMessage]);

  return (
    <CatalogItemPickerProvider>
      <Box
        className={`records-container${table.getIsSomeRowsSelected() ? " selection" : ""}`}
        sx={{
          // Hoisted off the rows: one rule here instead of one serialised sx per
          // row, which matters at 500 or 1000 records per page.
          '& tr:hover .catalog-item-option-actions': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        <MaterialReactTable table={table} />
      </Box>
    </CatalogItemPickerProvider>
  );
};

export default TableView;